// src/Routes/LandingPage/LandingPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
// @ts-ignore
import { motion } from "framer-motion";
import { BsArrowRight, BsLightningCharge, BsStar } from "react-icons/bs";
import { FaRegLightbulb, FaBrain } from "react-icons/fa";
import {
  HiOutlineSparkles,
  HiOutlineClock,
  HiOutlineAcademicCap,
} from "react-icons/hi";
import NavigationBar from "../../Components/HighLevelComponents/NavigationBar/NavigationBar";
import PageWrapper from "../../Components/Layout/PageWrapper/PageWrapper";

// Demo images
const demoImage =
  "https://res.cloudinary.com/dresq8wls/image/upload/v1746746108/demo_l2j3h3.png";
const demoQuestionsImage =
  "https://res.cloudinary.com/dresq8wls/image/upload/v1746746108/demo_1_hhpblm.png";
const aiDemoImage =
  "https://res.cloudinary.com/dresq8wls/image/upload/v1746746108/ai-demo_vvdcdv.png";

// Modern button component with gradient
const GradientButton: React.FC<{
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  secondary?: boolean;
}> = ({ onClick, children, className = "", secondary = false }) => (
  <button
    onClick={onClick}
    className={`
      ${
        secondary
          ? "bg-white text-gray-800 border border-gray-200 hover:bg-gray-50"
          : "bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 hover:shadow-lg"
      }
      px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:-translate-y-0.5
      ${className}
    `}
  >
    {children}
  </button>
);

// Feature card component
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}> = ({ icon, title, description, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
  >
    <div className="text-yellow-500 mb-5 text-3xl">{icon}</div>
    <h3 className="text-xl font-bold text-gray-800 mb-3">{title}</h3>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </motion.div>
);

// Tool card component
const ToolCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  comingSoon?: boolean;
  delay?: number;
}> = ({ icon, title, description, comingSoon = false, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden relative border border-gray-100"
  >
    {comingSoon && (
      <div className="absolute top-4 right-4 bg-blue-500 text-white text-xs font-medium px-2.5 py-1 rounded-full z-10">
        Coming Soon
      </div>
    )}

    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

    <div className="p-8 relative z-10">
      <div className="text-yellow-500 mb-5 text-3xl group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

// Stats item component
const StatItem: React.FC<{
  value: string;
  label: string;
  delay?: number;
}> = ({ value, label, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className="text-center"
  >
    <div className="text-4xl font-extrabold text-gray-800 mb-1">{value}</div>
    <div className="text-gray-500 font-medium">{label}</div>
  </motion.div>
);

// Testimonial component
const Testimonial: React.FC<{
  quote: string;
  author: string;
  role: string;
  delay?: number;
}> = ({ quote, author, role, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-8 shadow-sm hover:shadow transition-all duration-300 relative border border-gray-100"
  >
    <div className="absolute top-8 left-8 text-yellow-200 opacity-10">
      <svg width="40" height="40" fill="currentColor" viewBox="0 0 32 32">
        <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
      </svg>
    </div>

    <p className="text-gray-700 mb-6 relative z-10">{quote}</p>

    <div className="flex items-center">
      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center text-white font-bold">
        {author.charAt(0)}
      </div>
      <div className="ml-3">
        <p className="font-bold text-gray-800">{author}</p>
        <p className="text-gray-500 text-sm">{role}</p>
      </div>
    </div>
  </motion.div>
);

// Pricing plan component
const PricingPlan: React.FC<{
  name: string;
  price: string;
  features: string[];
  popular?: boolean;
  delay?: number;
}> = ({ name, price, features, popular = false, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className={`
      relative overflow-hidden rounded-xl
      ${
        popular
          ? "bg-gradient-to-b from-white to-yellow-50 border-2 border-yellow-400 shadow-md"
          : "bg-white border border-gray-200 shadow-sm"
      }
    `}
  >
    {popular && (
      <div className="absolute top-0 right-0 bg-yellow-400 text-xs font-bold px-3 py-1 text-center transform translate-x-8 translate-y-5 rotate-45">
        POPULAR
      </div>
    )}

    <div className="p-8">
      <h3 className="text-xl font-bold text-gray-800">{name}</h3>

      <div className="mt-5 flex items-baseline">
        <span className="text-5xl font-extrabold text-gray-900">${price}</span>
        <span className="ml-1 text-lg text-gray-500">/mo</span>
      </div>

      <div className="mt-8 space-y-4">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start">
            <div className="flex-shrink-0 h-5 w-5 text-green-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="ml-3 text-base text-gray-600">{feature}</p>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Link to="/signup" className="no-underline">
          <GradientButton secondary={!popular} className="w-full">
            <span className="flex items-center justify-center">
              Get Started {popular && <BsArrowRight className="ml-2" />}
            </span>
          </GradientButton>
        </Link>
      </div>
    </div>
  </motion.div>
);

// FAQ item component
const FaqItem: React.FC<{
  question: string;
  answer: string;
  delay?: number;
}> = ({ question, answer, delay = 0 }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: delay * 0.1 }}
      viewport={{ once: true }}
      className="border-b border-gray-200 pb-6"
    >
      <button
        className="flex justify-between items-center w-full text-left focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-medium text-gray-900">{question}</h3>
        <span className="ml-6 flex-shrink-0 text-gray-500">
          {isOpen ? (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          ) : (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </span>
      </button>
      {isOpen && (
        <div className="mt-3">
          <p className="text-base text-gray-600">{answer}</p>
        </div>
      )}
    </motion.div>
  );
};

// Custom Tools Section Component
const CustomToolsSection = () => {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background decoration elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-[10%] bottom-[10%] h-64 w-64 rounded-full bg-yellow-300 opacity-10 mix-blend-multiply blur-3xl filter"></div>
        <div className="absolute right-[15%] top-[20%] h-72 w-72 rounded-full bg-yellow-400 opacity-10 mix-blend-multiply blur-3xl filter"></div>
      </div>

      <div className="container mx-auto px-4 relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
            <span className="text-yellow-500">No-Code AI Tools</span> for Your
            Unique Needs
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Create your own custom AI solutions that save hours every week by
            automating your most repetitive tasks
          </p>
        </motion.div>

        {/* Main content grid */}
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* Left column - Feature description */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-6">
              Build Your Own AI Toolkit Without Any Coding
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Go beyond pre-built tools with our powerful Custom Tools feature.
              Create specialized AI assistants designed exactly for your
              repetitive workflows, whether you're generating feedback, planning
              lessons, or analyzing content.
            </p>

            <div className="space-y-6">
              {/* Feature highlights */}
              <ul className="space-y-3">
                {[
                  "Design custom tools with exactly the outputs you need",
                  "Automate your most time-consuming repetitive tasks",
                  "Save hours every week with one-click workflows",
                  "Build your personal library of specialized AI assistants",
                ].map((benefit, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 * i }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                    <svg
                      className="h-5 w-5 text-yellow-500 mt-0.5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-700">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <Link to="/custom-tools-landing" className="no-underline">
                <button className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 hover:shadow-lg px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:-translate-y-0.5 flex items-center">
                  Discover Custom AI Tools
                  <svg
                    className="ml-2 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
              </Link>
            </div>
          </motion.div>

          {/* Right column - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-300 opacity-50 rounded-2xl transform rotate-2"></div>
            <div className="relative overflow-hidden rounded-xl shadow-xl border-8 border-white">
              <img
                src={aiDemoImage}
                alt="AI Question Generator in action"
                className="w-full h-auto"
              />
            </div>

            {/* Testimonial card */}
            <div className="absolute bottom-4 right-4 bg-white rounded-xl p-4 shadow-md border border-gray-100 max-w-xs">
              <p className="text-sm text-gray-700 mb-2">
                "I created a custom feedback tool that analyzes student work and
                generates personalized comments. What took 20 minutes now
                happens in seconds."
              </p>
              <p className="text-xs font-medium text-gray-800">
                — Sarah J., High School Teacher
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const LandingPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>
          AI Question Generator: Create Multiple Choice, True/False Questions &
          Study Tools | Yippity
        </title>
        <meta
          name="description"
          content="Transform any text into quizzes, flashcards and study materials with our AI question generator. Create multiple choice, true/false, fill-in-the-blank questions instantly."
        />
        <meta
          name="keywords"
          content="ai question generator, multiple choice question generator, true or false generator, quiz generator, flashcard generator, test question generator, question generator from text"
        />
        <link rel="canonical" href="https://yippity.io" />
      </Helmet>

      <PageWrapper>
        <NavigationBar />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white pt-20 pb-28">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute left-[10%] top-[5%] h-64 w-64 rounded-full bg-yellow-300 opacity-20 mix-blend-multiply blur-3xl filter"></div>
            <div className="absolute right-[10%] top-[30%] h-72 w-72 rounded-full bg-yellow-400 opacity-20 mix-blend-multiply blur-3xl filter"></div>
          </div>

          <div className="container relative mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                  AI Question Generator for{" "}
                  <span className="text-yellow-500">Smarter</span> Studying
                </h1>
                <p className="mt-6 text-xl text-gray-600 leading-relaxed">
                  Transform any text into quizzes, flashcards, and study
                  materials in seconds with our powerful AI tools.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/signup" className="no-underline">
                    <GradientButton>Start Generating Questions</GradientButton>
                  </Link>
                  <p className="w-full mt-2 text-sm text-gray-500">
                    No credit card required
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-300 opacity-50 rounded-2xl transform rotate-3"></div>
                <div className="relative overflow-hidden rounded-xl shadow-2xl border-8 border-white">
                  <img
                    src={aiDemoImage}
                    alt="AI Question Generator in action"
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-14 bg-white border-y border-gray-100">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <StatItem value="1M+" label="Questions Generated" delay={0} />
              <StatItem value="50K+" label="Active Students" delay={1} />
              <StatItem value="5,000+" label="Educators" delay={2} />
              <StatItem value="4.8/5" label="User Rating" delay={3} />
            </div>
          </div>
        </section>

        {/* AI Tools Section */}
        <section className="py-24 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Powerful AI Study Tools
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Our suite of AI-powered tools helps you create effective study
                materials in seconds.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              <ToolCard
                icon={<HiOutlineAcademicCap className="w-10 h-10" />}
                title="Question Generator"
                description="Create open-ended questions with answers from any text to test knowledge and understanding."
                delay={0}
              />
              <ToolCard
                icon={<BsLightningCharge className="w-10 h-10" />}
                title="Multiple Choice Creator"
                description="Generate multiple choice questions with options for effective assessment and exam preparation."
                delay={1}
              />
              <ToolCard
                icon={<FaBrain className="w-10 h-10" />}
                title="Flashcard Generator"
                description="Transform study material into flashcards for efficient memorization and quick review."
                delay={2}
              />
              <ToolCard
                icon={<HiOutlineSparkles className="w-10 h-10" />}
                title="True/False Generator"
                description="Create true/false questions to test factual knowledge and comprehension."
                comingSoon
                delay={3}
              />
              <ToolCard
                icon={<FaRegLightbulb className="w-10 h-10" />}
                title="Fill-in-the-Blank Maker"
                description="Generate fill-in-the-blank questions to test vocabulary and key concepts."
                comingSoon
                delay={4}
              />
              <ToolCard
                icon={<HiOutlineClock className="w-10 h-10" />}
                title="Study Guide Creator"
                description="Create comprehensive study guides summarizing key points from your material."
                comingSoon
                delay={5}
              />
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute right-[5%] bottom-[5%] h-80 w-80 rounded-full bg-yellow-300 opacity-10 mix-blend-multiply blur-3xl filter"></div>
            <div className="absolute left-[10%] bottom-[30%] h-64 w-64 rounded-full bg-yellow-400 opacity-10 mix-blend-multiply blur-3xl filter"></div>
          </div>

          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Why Choose Our AI Question Generator
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Designed to help students and educators create high-quality
                study materials in seconds.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
              <FeatureCard
                icon={<HiOutlineSparkles className="w-10 h-10" />}
                title="Advanced AI Technology"
                description="Our state-of-the-art AI algorithms generate high-quality questions that test true understanding and critical thinking."
                delay={0}
              />
              <FeatureCard
                icon={<HiOutlineAcademicCap className="w-10 h-10" />}
                title="Multiple Question Types"
                description="Create a variety of question formats including multiple choice, true/false, open-ended, and fill-in-the-blank questions."
                delay={1}
              />
              <FeatureCard
                icon={<BsLightningCharge className="w-10 h-10" />}
                title="Lightning Fast"
                description="Generate dozens of questions in seconds, saving you hours of manual work and preparation time."
                delay={2}
              />
              <FeatureCard
                icon={<FaRegLightbulb className="w-10 h-10" />}
                title="For Teachers & Students"
                description="Perfect for educators creating assessments or students preparing for exams and reinforcing learning."
                delay={3}
              />
              <FeatureCard
                icon={<HiOutlineClock className="w-10 h-10" />}
                title="Time-Saving"
                description="What would take hours to create manually can be done in seconds with our intelligent AI assistant."
                delay={4}
              />
              <FeatureCard
                icon={<FaBrain className="w-10 h-10" />}
                title="Evidence-Based Learning"
                description="Active recall through quizzing is scientifically proven to improve long-term knowledge retention and test performance."
                delay={5}
              />
            </div>
          </div>
        </section>

        {/* Demo Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                  Edit & Export Your Quizzes
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Our intuitive editor lets you review and refine your generated
                  questions. Export to share with others or test yourself.
                </p>
                <ul className="space-y-3">
                  {[
                    "Edit questions and answers as needed",
                    "Organize and categorize your questions",
                    "Export in multiple formats",
                    "Share with students or study groups",
                    "Take practice tests to track progress",
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 * i }}
                      viewport={{ once: true }}
                      className="flex items-start"
                    >
                      <svg
                        className="h-6 w-6 text-green-500 mt-0.5 mr-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-gray-700">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-300 opacity-50 rounded-2xl transform -rotate-2"></div>
                <div className="relative overflow-hidden rounded-xl shadow-2xl border-8 border-white">
                  <img
                    src={demoQuestionsImage}
                    alt="Quiz editing interface"
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-24 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Loved by Students & Educators
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Discover how our AI question generator is transforming study
                habits and teaching methods.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <Testimonial
                quote="Yippity has completely transformed how I prepare for med school exams. The AI generates questions that really test my understanding, not just my memory."
                author="Sarah K."
                role="Medical Student"
                delay={0}
              />
              <Testimonial
                quote="As a high school teacher, this tool saves me hours of work every week. The questions are thoughtful and my students are more engaged with the material."
                author="Michael R."
                role="Science Teacher"
                delay={1}
              />
              <Testimonial
                quote="The quality of multiple choice questions is outstanding. They've helped me identify weak spots in my knowledge and focus my study time more effectively."
                author="James T."
                role="Computer Science Major"
                delay={2}
              />
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Start for free and upgrade when you're ready for unlimited
                access.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <PricingPlan
                name="Free"
                price="0"
                features={[
                  "1 Free AI quiz generation per month",
                  "Basic question types",
                  "Save and share your quizzes",
                  "Access to all question formats",
                  "Standard AI model",
                ]}
                delay={0}
              />

              <PricingPlan
                name="Premium"
                price="7.99"
                features={[
                  "Unlimited AI question generation",
                  "All AI tools included",
                  "Priority access to new features",
                  "Enhanced AI accuracy",
                  "Advanced customization options",
                  "Early adopter price lock",
                ]}
                popular={true}
                delay={1}
              />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="mt-12 max-w-xl mx-auto text-center"
            >
              <p className="text-sm text-gray-500">
                <span className="font-medium text-yellow-600">
                  ✨ Early Access Pricing:
                </span>{" "}
                Lock in our current rate forever as we add more AI tools and
                features.
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Everything you need to know about our AI question generator.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto divide-y divide-gray-200">
              <FaqItem
                question="How does the AI question generator work?"
                answer="Our AI analyzes your text to identify key concepts, facts, and relationships between ideas. It then generates various types of questions based on this analysis, creating effective study materials that test true understanding rather than mere memorization."
                delay={0}
              />
              <FaqItem
                question="What types of questions can I create?"
                answer="You can create multiple choice questions, true/false questions, open-ended questions, and fill-in-the-blank questions. We're constantly adding new question types to help you learn more effectively."
                delay={1}
              />
              <FaqItem
                question="Can I use this for my classroom or teaching?"
                answer="Absolutely! Many educators use our platform to create quizzes, tests, and study materials for their students. It saves time and helps create more engaging learning materials."
                delay={2}
              />
              <FaqItem
                question="How accurate are the generated questions?"
                answer="Our AI generates high-quality questions based on the text you provide. While it's very accurate, we recommend reviewing the questions before use, especially for critical assessments. The premium plan offers enhanced accuracy and quality."
                delay={3}
              />
              <FaqItem
                question="Is there a limit to how many questions I can generate?"
                answer="Free users can generate up to 1 set of questions per month. Premium subscribers have unlimited access to question generation and all our AI tools."
                delay={4}
              />
              <FaqItem
                question="Can I import content from PDFs or websites?"
                answer="Yes, our AI can process text from various sources. Simply copy and paste the content or upload your document, and our system will generate questions based on that material."
                delay={5}
              />
            </div>
          </div>
        </section>

        <CustomToolsSection />

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-yellow-50 to-yellow-100 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute right-[10%] bottom-[10%] h-64 w-64 rounded-full bg-yellow-300 opacity-20 mix-blend-multiply blur-3xl filter"></div>
            <div className="absolute left-[5%] top-[10%] h-72 w-72 rounded-full bg-yellow-300 opacity-20 mix-blend-multiply blur-3xl filter"></div>
          </div>

          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Ready to Transform Your Study Experience?
              </h2>
              <p className="text-xl text-gray-700 mb-10 leading-relaxed max-w-3xl mx-auto">
                Join thousands of students and educators using our AI question
                generator to create better study materials in less time.
              </p>
              <Link to="/signup" className="no-underline">
                <GradientButton className="px-8 py-4 text-lg">
                  Get Started for Free
                </GradientButton>
              </Link>
              <p className="text-gray-600 text-sm mt-6">
                No credit card required
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-16 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col items-center text-center">
                <h3 className="text-gray-800 font-bold mb-4">Yippity</h3>
                <p className="text-gray-600 max-w-md mb-8">
                  Advanced AI tools for students and educators to create
                  effective study materials instantly.
                </p>

                <div className="mt-4 pt-6 border-t border-gray-200 w-full flex flex-col items-center">
                  <p className="text-gray-500 text-sm mb-2">
                    © 2025 Yippity. All rights reserved.
                  </p>
                  <div className="flex space-x-4 mb-4">
                    <a
                      href="https://x.com/juan_two_juan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Twitter
                    </a>
                    <a
                      href="https://www.linkedin.com/in/juan-quintero-ovalle/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      LinkedIn
                    </a>
                  </div>
                  <div className="flex space-x-4">
                    <a
                      href="/privacy"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Privacy Policy
                    </a>
                    <a
                      href="/terms"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Terms of Service
                    </a>
                    <a
                      href="https://promptologer.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Powered By Promptologer
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </PageWrapper>
    </>
  );
};

export default LandingPage;
